import {featurePopoverRef} from '../FeaturePopover/FeaturePopover.root';
import {featuresIDs, shouldShowFeature} from '../FeaturePopover/constants/map';
import {
    getHasAnalyticsPermissions,
    openAnalytics,
    setIsNavigationEnabled
} from '../../_shared/GWTBridge/invoke/genPage';
import {sendAnalyticsMonitor} from '../../utils/monitoring';
import {monitorTransactionValues, Pages} from './consts';
import {reportGeneratorsStore} from './stores/ReportGeneratorsStore';
import {serializeReportParams} from './serialize';
import {ReportParam} from './types';
import {getChartData} from './services/reports';
import {TreeNodeProps} from '@priority-software/priority-style-react';
import {getExpressionId, getFormToUpdate, updateTree, clearOuterJoinSelection} from './utils';

export type ReportInputData = {
    EditField: ReportParam[];
};

export const openReportsGenerator = (session: string) => {
    reportGeneratorsStore.open(session);
};

export const closeReportsGenerator = session => {
    reportGeneratorsStore.close(session);
};

export const hideReportsGenerator = session => {
    reportGeneratorsStore.hide(session);
};

export const onRunChart = async (reportInputData: ReportInputData, reportId: string) => {
    const currentScreen = reportGeneratorsStore?.currentScreen;
    if (currentScreen) {
        const serializedReportParams = serializeReportParams(reportInputData?.EditField) || [];
        const data = await getChartData(parseInt(reportId, 10), serializedReportParams);
        if (data.Messages.length > 0) {
            // Handle messages
        } else {
            const chartHeader = {...data.Header, Inputs: data.Inputs};
            currentScreen.setChartHeader(chartHeader);
            currentScreen.setReportParams(serializedReportParams);
            currentScreen.setChartData({
                Columns: data.Columns,
                Rows: data.Rows,
                Companies: data.Companies
            });
            currentScreen.setCurrentPage(Pages.chartDisplay);
        }
    }
};

const monitorClosePopover = isOk => {
    if (isOk) {
        sendAnalyticsMonitor(monitorTransactionValues.AnalyticsPromoOk);
    } else {
        sendAnalyticsMonitor(monitorTransactionValues.AnalyticsPromoX);
    }
};

export const showAnalyticsFeaturePopover = (anchor, entName?) => {
    const featureID = !entName ? featuresIDs.analytics : featuresIDs.analytics2;

    if (shouldShowFeature(featureID)) {
        if (featurePopoverRef?.current?.state.open) {
            featurePopoverRef?.current?.onClose(true);
            setTimeout(() => {
                showFeaturePopover(featureID, anchor);
            }, 0);
        }
        showFeaturePopover(featureID, anchor);
        return true;
    }
    return false;
};

export const handleToggleFieldsDialog = (isOpen: boolean) => {
    reportGeneratorsStore.currentScreen.setIsFieldsDialogOpen();
    setIsNavigationEnabled(!isOpen);
};

export const handleAddExpression = () => {
    const expressionId = getExpressionId();
    reportGeneratorsStore.currentScreen.currentReport.addExpression(expressionId);
    sendAnalyticsMonitor(monitorTransactionValues.AnalyticsSetAddExpression);
};

export const handleOpenFieldSelect = async () => {
    handleToggleFieldsDialog(true);
    sendAnalyticsMonitor(
        monitorTransactionValues.AnalyticsSetAddFields,
        'success',
        reportGeneratorsStore.currentScreen.currentReport.title
    );
};

const showFeaturePopover = (featureID, anchor) => {
    featurePopoverRef?.current?.showFeaturePopover(featureID, anchor, monitorClosePopover, () => {
        if (getHasAnalyticsPermissions()) {
            sendAnalyticsMonitor(monitorTransactionValues.AnalyticsPromoYes);
            openAnalytics();
        }
    });
};

export const initOuterJoinMap = (nodeId: string): [TreeNodeProps] => {
    const {forms} = reportGeneratorsStore.currentScreen;
    const node = {
        title: forms?.get(nodeId)?.Title,
        id: nodeId,
        isMain: true,
        nodes: []
    };
    return [node];
};

export const updateOuterJoinMap = (
    node: TreeNodeProps,
    parent: {id: string; title: string} = null
) => {
    const outerJoinMap = reportGeneratorsStore?.currentScreen?.currentReport?.getOuterJoinMap();
    if (!outerJoinMap) {
        return;
    }
    if (parent) {
        const formToUpdate = getFormToUpdate(outerJoinMap, parent.id, node.extraData?.isZoomed);
        if (formToUpdate) {
            const nodeIndexInForm = formToUpdate.nodes.findIndex(item => item.id === node.id);
            const isFieldInForm = nodeIndexInForm >= 0;
            !isFieldInForm && formToUpdate.nodes.push(node);
        }
    } else {
        // Forms zoomed to from base form
        const isInMap = !!outerJoinMap.find(item => item.id === node.id);
        if (isInMap) {
            return;
        } else {
            outerJoinMap.push(node);
        }
    }
    reportGeneratorsStore?.currentScreen?.currentReport?.setOuterJoinMap(outerJoinMap);
};

export const resetOuterJoinId = () => {
    const currentReport = reportGeneratorsStore?.currentScreen?.currentReport;
    if (!currentReport) {
        return;
    }
    const {savedOuterJoinId, getOuterJoinMap, setOuterJoinMap} = currentReport;
    let updatedTree;
    if (savedOuterJoinId) {
        currentReport.setOuterJoinId(savedOuterJoinId);
        updatedTree = updateTree(getOuterJoinMap(), savedOuterJoinId.toString());
    } else {
        currentReport.setOuterJoinId(0);
        updatedTree = clearOuterJoinSelection(getOuterJoinMap());
    }
    setOuterJoinMap(updatedTree);
};
